import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNotify } from 'react-admin';
import axios from 'axios';
import config from '../../../config/config.json';

const host = config.production.host;

const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const TeamPenalties = ({ teamId }) => {
    const notify = useNotify();
    const [penalties, setPenalties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPenalty, setEditingPenalty] = useState(null);
    const [leghe, setLeghe] = useState([]);
    const [stagioni, setStagioni] = useState([]);
    const [legheStagioni, setLegheStagioni] = useState([]);
    const [formData, setFormData] = useState({
        legaId: '',
        stagioneId: '',
        legaStagioneId: '',
        punti: 0,
        motivazione: '',
        dataAssegnazione: new Date().toISOString().split('T')[0],
        note: '',
        flVisibile: 1,
        flAttivo: 1
    });

    useEffect(() => {
        loadPenalties();
        loadLeghe();
        loadStagioni();
    }, [teamId]);

    useEffect(() => {
        if (formData.legaId && formData.stagioneId) {
            loadLegaStagione();
        }
    }, [formData.legaId, formData.stagioneId]);

    const loadPenalties = async () => {
        setLoading(true);
        try {
            const token = getJwt();
            const response = await axios.get(`${host}/tbl-penalita`, {
                params: {
                    teamId: teamId,
                    $sort: { createdAt: -1 }
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setPenalties(response.data.data || []);
        } catch (error) {
            console.error('Errore caricamento penalità:', error);
            notify('Errore nel caricamento delle penalità', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadLeghe = async () => {
        try {
            const token = getJwt();
            const response = await axios.get(`${host}/tbl-lega`, {
                params: { $limit: 100 },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error('Errore caricamento leghe:', error);
        }
    };

    const loadStagioni = async () => {
        try {
            const token = getJwt();
            const response = await axios.get(`${host}/tbl-stagione`, {
                params: { $limit: 100 },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setStagioni(response.data.data || []);
        } catch (error) {
            console.error('Errore caricamento stagioni:', error);
        }
    };

    const loadLegaStagione = async () => {
        try {
            const token = getJwt();
            const response = await axios.get(`${host}/tbl-lega-stagione`, {
                params: {
                    legaId: formData.legaId,
                    stagioneId: formData.stagioneId
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = response.data.data || [];
            setLegheStagioni(data);
            if (data.length > 0) {
                setFormData(prev => ({ ...prev, legaStagioneId: data[0].id }));
            }
        } catch (error) {
            console.error('Errore caricamento lega-stagione:', error);
        }
    };

    const handleOpenDialog = (penalty = null) => {
        if (penalty) {
            setEditingPenalty(penalty);
            setFormData({
                legaId: penalty.legaId,
                stagioneId: penalty.stagioneId,
                legaStagioneId: penalty.legaStagioneId,
                punti: penalty.punti,
                motivazione: penalty.motivazione || '',
                dataAssegnazione: penalty.dataAssegnazione ? new Date(penalty.dataAssegnazione).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                note: penalty.note || '',
                flVisibile: penalty.flVisibile,
                flAttivo: penalty.flAttivo
            });
        } else {
            setEditingPenalty(null);
            setFormData({
                legaId: '',
                stagioneId: '',
                legaStagioneId: '',
                punti: 0,
                motivazione: '',
                dataAssegnazione: new Date().toISOString().split('T')[0],
                note: '',
                flVisibile: 1,
                flAttivo: 1
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPenalty(null);
    };

    const handleSave = async () => {
        if (!formData.legaId || !formData.stagioneId || !formData.legaStagioneId) {
            notify('Seleziona lega e stagione', { type: 'warning' });
            return;
        }

        try {
            const token = getJwt();
            const payload = {
                teamId: parseInt(teamId),
                legaId: parseInt(formData.legaId),
                stagioneId: parseInt(formData.stagioneId),
                legaStagioneId: parseInt(formData.legaStagioneId),
                punti: parseFloat(formData.punti),
                motivazione: formData.motivazione,
                dataAssegnazione: formData.dataAssegnazione,
                note: formData.note,
                flVisibile: formData.flVisibile,
                flAttivo: formData.flAttivo,
                flAnnullato: 0
            };

            if (editingPenalty) {
                await axios.patch(`${host}/tbl-penalita/${editingPenalty.id}`, payload, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                notify('Penalità aggiornata con successo', { type: 'success' });
            } else {
                await axios.post(`${host}/tbl-penalita`, payload, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                notify('Penalità creata con successo', { type: 'success' });
            }

            handleCloseDialog();
            loadPenalties();
        } catch (error) {
            console.error('Errore salvataggio penalità:', error);
            notify('Errore nel salvataggio della penalità', { type: 'error' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Sei sicuro di voler eliminare questa penalità?')) return;

        try {
            const token = getJwt();
            await axios.patch(`${host}/tbl-penalita/${id}`, {
                flAnnullato: 1
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            notify('Penalità eliminata con successo', { type: 'success' });
            loadPenalties();
        } catch (error) {
            console.error('Errore eliminazione penalità:', error);
            notify('Errore nell\'eliminazione della penalità', { type: 'error' });
        }
    };

    const toggleVisibility = async (penalty) => {
        try {
            const token = getJwt();
            await axios.patch(`${host}/tbl-penalita/${penalty.id}`, {
                flVisibile: penalty.flVisibile === 1 ? 0 : 1
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            notify('Visibilità aggiornata', { type: 'success' });
            loadPenalties();
        } catch (error) {
            console.error('Errore aggiornamento visibilità:', error);
            notify('Errore nell\'aggiornamento', { type: 'error' });
        }
    };

    const getTotalPenalties = () => {
        return penalties
            .filter(p => p.flAnnullato === 0 && p.flAttivo === 1)
            .reduce((sum, p) => sum + parseFloat(p.punti || 0), 0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header con totale penalità */}
            <Card sx={{ mb: 3, borderLeft: '4px solid #f44336' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>
                                ⚠️ Penalità e Bonus
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Gestisci le penalità in punti per questa squadra
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ 
                                    color: getTotalPenalties() < 0 ? '#f44336' : '#4caf50',
                                    fontWeight: 'bold'
                                }}>
                                    {getTotalPenalties() > 0 ? '+' : ''}{getTotalPenalties().toFixed(2)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Totale Punti
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                fullWidth
                            >
                                Aggiungi Penalità/Bonus
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tabella penalità */}
            {penalties.length === 0 ? (
                <Alert severity="info">
                    Nessuna penalità registrata per questa squadra
                </Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Lega</strong></TableCell>
                                <TableCell><strong>Stagione</strong></TableCell>
                                <TableCell align="center"><strong>Punti</strong></TableCell>
                                <TableCell><strong>Motivazione</strong></TableCell>
                                <TableCell><strong>Data</strong></TableCell>
                                <TableCell align="center"><strong>Stato</strong></TableCell>
                                <TableCell align="center"><strong>Azioni</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {penalties.map((penalty) => (
                                <TableRow 
                                    key={penalty.id}
                                    sx={{ 
                                        opacity: penalty.flAnnullato === 1 ? 0.5 : 1,
                                        backgroundColor: penalty.flAnnullato === 1 ? '#f5f5f5' : 'inherit'
                                    }}
                                >
                                    <TableCell>{penalty.legaId}</TableCell>
                                    <TableCell>{penalty.stagioneId}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={parseFloat(penalty.punti) < 0 ? <RemoveCircleIcon /> : <AddCircleIcon />}
                                            label={`${parseFloat(penalty.punti) > 0 ? '+' : ''}${parseFloat(penalty.punti).toFixed(2)}`}
                                            color={parseFloat(penalty.punti) < 0 ? 'error' : 'success'}
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {penalty.motivazione || 'N/A'}
                                        </Typography>
                                        {penalty.note && (
                                            <Typography variant="caption" color="textSecondary">
                                                Note: {penalty.note}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {penalty.dataAssegnazione ? 
                                            new Date(penalty.dataAssegnazione).toLocaleDateString('it-IT') : 
                                            'N/A'
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <Chip
                                                label={penalty.flAttivo === 1 ? 'Attiva' : 'Inattiva'}
                                                color={penalty.flAttivo === 1 ? 'success' : 'default'}
                                                size="small"
                                            />
                                            <Chip
                                                label={penalty.flVisibile === 1 ? 'Visibile' : 'Nascosta'}
                                                color={penalty.flVisibile === 1 ? 'primary' : 'default'}
                                                size="small"
                                            />
                                            {penalty.flAnnullato === 1 && (
                                                <Chip
                                                    label="Annullata"
                                                    color="error"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <Tooltip title={penalty.flVisibile === 1 ? 'Nascondi' : 'Mostra'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleVisibility(penalty)}
                                                    disabled={penalty.flAnnullato === 1}
                                                >
                                                    {penalty.flVisibile === 1 ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Modifica">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDialog(penalty)}
                                                    disabled={penalty.flAnnullato === 1}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Elimina">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(penalty.id)}
                                                    disabled={penalty.flAnnullato === 1}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog per aggiungere/modificare penalità */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingPenalty ? '✏️ Modifica Penalità' : '➕ Aggiungi Penalità/Bonus'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Lega</InputLabel>
                                    <Select
                                        value={formData.legaId}
                                        onChange={(e) => setFormData({ ...formData, legaId: e.target.value })}
                                        label="Lega"
                                    >
                                        {leghe.map((lega) => (
                                            <MenuItem key={lega.id} value={lega.id}>
                                                {lega.nome}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Stagione</InputLabel>
                                    <Select
                                        value={formData.stagioneId}
                                        onChange={(e) => setFormData({ ...formData, stagioneId: e.target.value })}
                                        label="Stagione"
                                    >
                                        {stagioni.map((stagione) => (
                                            <MenuItem key={stagione.id} value={stagione.id}>
                                                {stagione.nome}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Punti"
                                    value={formData.punti}
                                    onChange={(e) => setFormData({ ...formData, punti: e.target.value })}
                                    helperText="Negativo per penalità, positivo per bonus"
                                    inputProps={{ step: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Assegnazione"
                                    value={formData.dataAssegnazione}
                                    onChange={(e) => setFormData({ ...formData, dataAssegnazione: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Motivazione"
                                    value={formData.motivazione}
                                    onChange={(e) => setFormData({ ...formData, motivazione: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Note"
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Visibilità</InputLabel>
                                    <Select
                                        value={formData.flVisibile}
                                        onChange={(e) => setFormData({ ...formData, flVisibile: e.target.value })}
                                        label="Visibilità"
                                    >
                                        <MenuItem value={1}>Visibile</MenuItem>
                                        <MenuItem value={0}>Nascosta</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Stato</InputLabel>
                                    <Select
                                        value={formData.flAttivo}
                                        onChange={(e) => setFormData({ ...formData, flAttivo: e.target.value })}
                                        label="Stato"
                                    >
                                        <MenuItem value={1}>Attiva</MenuItem>
                                        <MenuItem value={0}>Inattiva</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Annulla</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {editingPenalty ? 'Aggiorna' : 'Crea'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
